import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  useCreateExport,
  useGetExecutiveDashboard,
  useGetExport,
} from '../../api/generated/reporting/reporting';
import type { ExecutiveDashboard as ExecutiveDashboardModel } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatNumber } from '../../i18n/datetime';
import { formatMoney } from '../billing/money';
import { currentPeriod, isPeriod } from '../payroll/period';
import styles from './dashboard.module.css';

/**
 * Indicadores executivos de uma competencia.
 *
 * **Nao ha grafico aqui, e isso e uma decisao e nao uma omissao.** O contrato
 * devolve doze escalares de **um** periodo: nenhuma serie temporal, nenhuma
 * categoria para comparar. Magnitude de um numero so se le melhor como numero;
 * um grafico de barra unica ou uma pizza de duas fatias seriam decoracao que
 * atrapalha. A forma certa para este dado e a fila de cartoes, com um numero
 * heroi, e medidor para as duas razoes contra o limite de 100%.
 *
 * Cada cartao carrega a **definicao adotada** num `details`, que e alcancavel
 * por teclado — `title` nao e, e nao chega a leitor de tela. Numero sem
 * definicao e numero que duas pessoas leem diferente, e as definicoes desta
 * onda ainda esperam ratificacao.
 */
export function ExecutiveDashboard() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  const [period, setPeriod] = useState(currentPeriod);
  const valid = isPeriod(period);

  const dashboard = useGetExecutiveDashboard(
    { period },
    { query: { enabled: !!me && valid } },
  );

  if (!me) return null;

  const data = dashboard.data;

  return (
    <div className={styles.executive}>
      <h1 className={styles.title}>{t('dashboard.executive.title')}</h1>

      <Field
        label={t('payroll.period')}
        placeholder={t('payroll.periodPlaceholder')}
        value={period}
        onChange={(event) => setPeriod(event.target.value)}
        error={valid ? undefined : t('payroll.periodInvalid')}
      />

      {dashboard.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      {/*
        A exportacao fica acima dos numeros e **nunca bloqueia a tela**: o PDF
        de uma competencia inteira nao cabe no tempo de uma requisicao, e por
        isso o contrato devolve 202 com um trabalho, e nao o arquivo.
      */}
      <ExportPanel period={valid ? period : currentPeriod()} />

      {data ? <Indicators data={data} locale={me.locale} /> : null}
    </div>
  );
}

function Indicators({
  data,
  locale,
}: {
  readonly data: ExecutiveDashboardModel;
  readonly locale: string;
}) {
  const { t } = useTranslation();

  const count = (value: number): string => formatNumber(value, locale);
  const money = (value: ExecutiveDashboardModel['recognizedRevenue']): string =>
    formatMoney(value, locale);

  return (
    <section className={styles.section} aria-labelledby="executive-indicators">
      <h2 id="executive-indicators" className={styles.sectionTitle}>
        {t('dashboard.executive.indicators')}
      </h2>
      <p className={styles.empty}>{t('dashboard.executive.definitionsPending')}</p>

      {/*
        Exatamente um numero heroi por tela. Receita reconhecida e a base de
        ticket medio e de margem: sem ela na frente, os outros dois sao numeros
        que ninguem consegue reconciliar.
      */}
      <p className={styles.hero}>
        <span className={styles.tileLabel}>{t('dashboard.executive.recognizedRevenue')}</span>
        <span className={styles.heroValue}>{money(data.recognizedRevenue)}</span>
      </p>
      <Definition text={t('dashboard.executive.definition.recognizedRevenue')} />

      <ul className={styles.tiles}>
        <Tile
          label={t('dashboard.executive.monthlyRecurringRevenue')}
          value={money(data.monthlyRecurringRevenue)}
          definition={t('dashboard.executive.definition.monthlyRecurringRevenue')}
        />
        <Tile
          label={t('dashboard.executive.averageTicket')}
          value={money(data.averageTicket)}
          definition={t('dashboard.executive.definition.averageTicket')}
        />
        <Tile
          label={t('dashboard.executive.teacherCost')}
          value={money(data.teacherCost)}
          definition={t('dashboard.executive.definition.teacherCost')}
        />
        <Tile
          label={t('dashboard.executive.estimatedMargin')}
          value={money(data.estimatedMargin)}
          definition={t('dashboard.executive.definition.estimatedMargin')}
        />
        <Tile
          label={t('dashboard.executive.activeStudents')}
          value={count(data.activeStudents)}
          definition={t('dashboard.executive.definition.activeStudents')}
        />
        <Tile
          label={t('dashboard.executive.activeTeachers')}
          value={count(data.activeTeachers)}
          definition={t('dashboard.executive.definition.activeTeachers')}
        />
        <Tile
          label={t('dashboard.executive.activeDuos')}
          value={count(data.activeDuos)}
          definition={t('dashboard.executive.definition.activeDuos')}
        />
        <Tile
          label={t('dashboard.executive.scheduledClasses')}
          value={count(data.scheduledClasses)}
          definition={t('dashboard.executive.definition.scheduledClasses')}
        />
        <Tile
          label={t('dashboard.executive.completedClasses')}
          value={count(data.completedClasses)}
          definition={t('dashboard.executive.definition.completedClasses')}
        />
      </ul>

      <ul className={styles.tiles}>
        <Meter
          label={t('dashboard.executive.attendanceRate')}
          rate={data.attendanceRate}
          locale={locale}
          tone="good"
          definition={t('dashboard.executive.definition.attendanceRate')}
        />
        <Meter
          label={t('dashboard.executive.delinquencyRate')}
          rate={data.delinquencyRate}
          locale={locale}
          tone="bad"
          definition={t('dashboard.executive.definition.delinquencyRate')}
        />
      </ul>
    </section>
  );
}

/**
 * A definicao adotada, num `details`.
 *
 * `summary` entra na ordem de tabulacao por natureza; `title` nao entra em
 * lugar nenhum que teclado ou leitor de tela alcancem.
 */
export function Definition({ text }: { readonly text: string }) {
  const { t } = useTranslation();
  return (
    <details className={styles.definition}>
      <summary>{t('dashboard.executive.definitionLabel')}</summary>
      <p>{text}</p>
    </details>
  );
}

/**
 * Exportado porque a home do Super Admin mostra um recorte destes mesmos
 * indicadores, e um numero sem a definicao adotada e um numero que duas pessoas
 * leem diferente. Copiar o cartao para la abriria a porta para as duas telas
 * divergirem na definicao — que e justamente o que o `details` existe para
 * impedir.
 */
export function Tile({
  label,
  value,
  definition,
}: {
  readonly label: string;
  readonly value: string;
  readonly definition: string;
}) {
  return (
    <li className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
      <Definition text={definition} />
    </li>
  );
}

/**
 * Razao contra o limite de 100%.
 *
 * A barra e decorativa e sai da arvore de acessibilidade: o numero em texto e
 * que carrega o valor, e por isso a cor nunca e o unico canal. O trilho e um
 * tom mais claro do mesmo token do preenchimento, para o estado ser legivel ao
 * longo da barra inteira.
 */
function Meter({
  label,
  rate,
  locale,
  tone,
  definition,
}: {
  readonly label: string;
  readonly rate: number;
  readonly locale: string;
  readonly tone: 'good' | 'bad';
  readonly definition: string;
}): ReactNode {
  const percent = formatNumber(rate, locale, { style: 'percent', maximumFractionDigits: 0 });
  const fill = tone === 'good' ? styles.meterFillGood : styles.meterFillBad;

  return (
    <li className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{percent}</span>
      <span className={styles.meterTrack} aria-hidden="true">
        <span
          className={`${styles.meterFill} ${fill}`}
          style={{ inlineSize: `${Math.round(rate * 100)}%` }}
        />
      </span>
      <Definition text={definition} />
    </li>
  );
}

/**
 * Exportacao assincrona em PDF.
 *
 * `pdf` e o unico formato: um segundo renderizador seria um segundo lugar para
 * esquecer a mascara. O trabalho nasce enfileirado, passa por processando e so
 * entao tem link — e `expired` e estado real, porque o artefato derivado dura
 * sete dias.
 */
/** Primeira espera do acompanhamento da exportacao, e o teto que ela alcanca. */
const FIRST_POLL_MS = 1_000;
const MAX_POLL_MS = 10_000;

function ExportPanel({ period }: { readonly period: string }) {
  const { t } = useTranslation();
  const [exportId, setExportId] = useState<string | null>(null);
  const create = useCreateExport();

  const job = useGetExport(exportId ?? '', {
    query: {
      enabled: exportId !== null,
      // Enquanto o trabalho anda, a tela pergunta de novo. Quando ele para de
      // andar, a repeticao para junto: fila que nunca deixa de bater e um
      // vazamento de requisicao contra o proprio servidor.
      //
      // O intervalo CRESCE em vez de ser fixo. Eram 300 ms — 3,3 requisicoes
      // por segundo por aba, durante os segundos ou minutos que o PDF leva —
      // contra um worker que acorda a cada 10 s (reporting/worker.go:45): cerca
      // de 33 leituras jogadas fora por tique, cada uma custando a resolucao da
      // sessao. Comecar em 1 s e dobrar ate o tique do worker mantem a primeira
      // resposta rapida, que e quando a pessoa esta olhando, e para de bater na
      // porta quando fica claro que o trabalho e longo.
      //
      // `dataUpdateCount` e o contador que o proprio React Query mantem por
      // consulta; guardar um contador nosso em `useState` faria a leitura
      // reagendar a si mesma a cada render.
      refetchInterval: (query) => {
        const state = query.state.data?.state;
        if (state !== 'queued' && state !== 'processing') return false;
        const step = Math.max(0, query.state.dataUpdateCount - 1);
        return Math.min(FIRST_POLL_MS * 2 ** step, MAX_POLL_MS);
      },
    },
  });

  const current = job.data ?? create.data ?? null;
  const running = current?.state === 'queued' || current?.state === 'processing';

  return (
    <section className={styles.section} aria-labelledby="executive-export">
      <h2 id="executive-export" className={styles.sectionTitle}>
        {t('dashboard.executive.export')}
      </h2>

      <Button
        variant="primary"
        loading={create.isPending}
        onClick={() =>
          create.mutate(
            { data: { kind: 'executive_dashboard', period, format: 'pdf' } },
            { onSuccess: (created) => setExportId(created.id) },
          )
        }
      >
        {t('dashboard.executive.exportAction')}
      </Button>

      {running ? (
        <p className={styles.empty} role="status">
          {t(`dashboard.executive.exportState.${current.state}`)}
        </p>
      ) : null}

      {current?.state === 'ready' && current.download ? (
        <p>
          <a href={current.download.url}>{t('dashboard.executive.exportDownload')}</a>
        </p>
      ) : null}

      {current?.state === 'expired' ? (
        <Alert tone="warning" title={t('dashboard.executive.exportExpiredTitle')}>
          {t('dashboard.executive.exportExpiredDetail')}
        </Alert>
      ) : null}

      {current?.state === 'failed' ? (
        <Alert tone="error">{t('dashboard.executive.exportFailed')}</Alert>
      ) : null}

      {/*
        A leitura do andamento tambem falha — 404 depois da retencao, queda de
        rede. Sem isto, a tela ficava presa em "na fila" para sempre, dizendo
        que algo caminha quando ninguem mais esta perguntando.
      */}
      {job.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      {create.error ? (
        <Alert tone="error">
          {isProblemError(create.error) && create.error.status === 403
            ? t('dashboard.executive.exportForbidden')
            : t('error.unexpected')}
        </Alert>
      ) : null}
    </section>
  );
}
